import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { ktx2Extend } from '../utils/ktx2';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { beatPulse } from '../utils/worldBeat';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';
import { API_BASE, IS_MOBILE, VIDEO_OFF, assetV } from '../config';
import { freeGltf } from '../utils/gltfMemory';
import { listWorldObjects } from '../services/worldObjects';
import { ROOMS } from '../rooms';
import { buildCityBatches, disposeCityBatches, CITY_BATCH_OFF } from '../utils/cityBatch';
import { NEXT_LOOK, QUALITY } from '../render/quality';
import { sky } from '../render/skyState';
import { patchStreetMaterial } from '../render/streetMaterials';
import { applyDerivedNormals } from '../render/detailNormals';
import { publishCity, clearCity } from '../render/cityBridge';
import { buildStreetCollision, clearStreetCollision } from '../game/streetCollision';
import { FEEL_CLASSIC } from '../game/walkBody';

// Sign "families" we drive with dynamic graphics. Identified with ?pick=1.
// A base name matches that mesh AND all its instances across the city
// (`<base>`, `<base>_2`, `<base>_3`, …), so one entry lights up every copy.
// Tall panels (portrait) suit HypeClips / album art. Add more as we pick them.
const SIGN_MESHES = ['pasted__pCube88_Pawnshop1GLOW_0'];
const signBaseFor = (name) => name && SIGN_MESHES.find((b) => name === b || name.startsWith(b + '_'));

// WebGL textures need CORS headers; GCS buckets don't send them. Stream the
// image through the API's /api/assets/file proxy (which carries cors()) so the
// texture loads cross-origin. Passes non-GCS URLs through untouched.
function proxied(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'storage.googleapis.com') {
      const parts = u.pathname.replace(/^\//, '').split('/');
      const bucket = parts.shift();
      const name = parts.map(decodeURIComponent).join('/');
      return `${API_BASE}/api/assets/file?bucket=${encodeURIComponent(bucket)}&name=${encodeURIComponent(name)}`;
    }
  } catch { /* fall through */ }
  return url;
}

// Give a sign mesh its own material, remapping its UVs to fill the panel (they
// map into a shared atlas region otherwise) and cloning the geometry so we
// don't disturb other meshes sharing it. The tall (portrait) signs play a
// HypeClip — a looping muted 9:16 video that fits the panel; clicking unmutes
// it. Returns the created <video> (or null) for cleanup + click-to-play.
function paintSign(mesh, { videoUrl, imageUrl }) {
  if (!mesh?.geometry) return null;
  const g = mesh.geometry.clone();
  const uv = g.attributes.uv;
  if (uv) {
    let minU = Infinity, minV = Infinity, maxU = -Infinity, maxV = -Infinity;
    for (let i = 0; i < uv.count; i++) {
      const u = uv.getX(i), v = uv.getY(i);
      minU = Math.min(minU, u); maxU = Math.max(maxU, u);
      minV = Math.min(minV, v); maxV = Math.max(maxV, v);
    }
    const du = (maxU - minU) || 1, dv = (maxV - minV) || 1;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) - minU) / du, (uv.getY(i) - minV) / dv);
    uv.needsUpdate = true;
  }
  mesh.geometry = g;

  let tex, video = null;
  if (videoUrl && !VIDEO_OFF) {
    video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = proxied(videoUrl);
    video.loop = true; video.muted = true; video.playsInline = true; video.preload = 'auto';
    video.play().catch(() => {});
    tex = new THREE.VideoTexture(video);
  } else {
    tex = new THREE.TextureLoader().load(
      proxied(imageUrl),
      (t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 16; t.needsUpdate = true; },
      undefined,
      () => console.warn('[sign] media failed to load:', imageUrl),
    );
  }
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 16;          // sharp at the steep viewing angles of a wall sign
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = videoUrl ? THREE.LinearFilter : THREE.LinearMipmapLinearFilter;
  // DoubleSide: imported sign faces often point inward, which FrontSide culls
  // (the sign would vanish). Unlit so it reads like a lit billboard.
  mesh.material = new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide, toneMapped: false });
  return video;
}

// CityModel — a pre-built stylized city (GLTF) as the walkable environment, so
// the whole streetscape is real modeled geometry, not textured boxes. The
// model ships at a huge authoring scale, so we measure its true world bounds
// after load and fit + ground it. Its own GLOW materials give night windows.
//
// It also carries a parked car (a Lotus "elise" under `pasted__group1`); we
// report its world position so the fly-in can land the walker right beside it.
//
// Preview mode: ?city=model (keeps OSM + the hand-built town intact).

// Phones load the mobile variant (512px WebP textures ≈ 1.7MB vs 13.7MB, ~4× less
// VRAM); desktop keeps the full-res original. Same node graph, so anchors + the
// hero car still resolve.
// Desktop gets a KTX2 build of the FULL city, not the decimated mobile one:
// same geometry, same 1024 textures, same node names — only the texture ENCODING
// differs. 13.7MB → 2.7MB, and because Basis/ETC1S stays compressed on the GPU
// it also drops ~41MB of texture VRAM to a few MB. The city was never heavy in
// triangles (31k); it was heavy in texture bytes, which is exactly what this
// fixes. `ccity_building_set_1.glb` is untouched — revert is this one line.
export const MODEL_URL = assetV(IS_MOBILE ? '/models/ccity_building_set_1_mobile.glb' : '/models/ccity_building_set_1_desktop.glb');
const FIT_METRES = 240; // fit the city's larger horizontal span to this

// Where the city stops being drawn. Matches the scene's fog FAR (PlanetScene:
// fog args [colour, 80, 260]) plus a margin, because past the fog's far value
// every pixel is already 100% fog colour. Phones only by default — a desktop has
// the headroom and popping is one more thing to get wrong on the machine we
// demo from — with ?cull=<units> to try it anywhere, and ?cull=0 to switch off.
const CULL_DIST = (() => {
  if (typeof window === 'undefined') return 0;
  const q = new URLSearchParams(window.location.search).get('cull');
  if (q !== null) return Math.max(0, Number(q) || 0);
  return IS_MOBILE ? 275 : 0;
})();

// FROM INSIDE A SITED ROOM, YOU ONLY NEED THE OTHER SIDE OF THE STREET.
//
// A public venue stands in the real city, so the city keeps rendering while you
// are in it — that is what its windows look out on. But the block opposite is
// itself tall enough to hide everything behind it, so drawing the rest of the
// city is paying for geometry an occluder already covers.
//
// Tight enough to keep the facing street and the sky above it, and it applies on
// DESKTOP TOO, unlike the street-level cull — indoors there is no wide view to
// protect, and this is what makes a sited interior affordable.
const SITED_CULL = 70;

// Venues anchor to real named buildings, chosen to be SPREAD ACROSS the city
// (so travelling between them is a real journey). The concert hall seeds on
// the Paramount theatre; the other three are farthest-point-sampled from the
// candidate landmarks so they land far apart.
const CONCERT_SEED = 'LM_Paramount1';
const ANCHOR_CANDIDATES = [
  'LM_Paramount1', 'LM_Paramount7', 'LM_Paramount9', 'LM_Liquor', 'LM_Clinic2',
  'LM_Laundrette', 'LM_PawnShop1', 'LM_PawnShop2', 'LM_PawnShop3',
  'Filler_GunShop', 'Filler_CityArms2', 'Filler_Liquor1', 'Filler_Housing',
  'Filler_Apartments2', 'Filler_Housing1', 'Filler_Housing2', 'Filler_GunShop1',
];
const VENUE_ORDER = ['concert-hall', 'studio', 'listening-room', 'record-store', 'theater', 'grocery', 'salon', 'subway'];
// Every key in VENUE_ORDER needs an entry: anchor sampling can come up short if
// a candidate building is missing from the model, and a venue with no anchor is
// a marker at `undefined` rather than a visible mistake.
const ANCHOR_FALLBACK = {
  'concert-hall': [55, 18, 40], studio: [-55, 18, -35],
  'listening-room': [45, 18, -50], 'record-store': [-45, 18, 55],
  theater: [0, 18, -55], grocery: [-58, 18, 18], salon: [58, 18, 5],
  subway: [18, 18, -58],
};

// The parked hero Lotus baked into the city model — found by its group name,
// or by any of its parts if a re-export renamed the group.
function findHeroCar(model) {
  let car = model.getObjectByName('pasted__group1');
  if (!car) model.traverse((o) => { if (!car && /elise|steeringwheel|_tire_/i.test(o.name || '')) car = o; });
  return car || null;
}

// Shrinks the hero car in place (idempotent). Hoisted out of the anchors effect
// so it can run BEFORE the city is batched: a batch bakes each mesh's matrix, so
// the car has to be at its final size first — otherwise it would be left out of
// the batches and cost ~30 draw calls a frame on its own.
function scaleHeroCar(car) {
  if (!car) return;

  // The baked hero Lotus is authored oversized vs the town's ~1.8m residents.
  // Shrink the car NODE in place, anchoring its base-centre in world space so it
  // stays parked on the same spot and grounded (no float / no drift). Tunable
  // live with ?herocar=<fraction> (0.6 = 60% of the baked size).
  const HERO_CAR_SCALE = (() => {
    const v = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get('herocar'));
    return Number.isFinite(v) && v > 0 ? v : 0.7;
  })();
  if (HERO_CAR_SCALE !== 1 && car.parent && !car.userData._heroScaled) {
    car.userData._heroScaled = true; // idempotent: never compound (StrictMode / re-runs)
    car.updateWorldMatrix(true, true);
    const b0 = new THREE.Box3().setFromObject(car);
    const c0 = b0.getCenter(new THREE.Vector3());
    const anchor0 = new THREE.Vector3(c0.x, b0.min.y, c0.z); // world base-centre before
    car.scale.multiplyScalar(HERO_CAR_SCALE);
    car.updateWorldMatrix(true, true);
    const b1 = new THREE.Box3().setFromObject(car);
    const c1 = b1.getCenter(new THREE.Vector3());
    const anchor1 = new THREE.Vector3(c1.x, b1.min.y, c1.z); // world base-centre after
    // Move the anchor back to where it was, converting the world delta into the
    // parent's local space (correct under the parent's fit scale/offset).
    const inv = new THREE.Matrix4().copy(car.parent.matrixWorld).invert();
    const localBack = anchor0.clone().applyMatrix4(inv).sub(anchor1.clone().applyMatrix4(inv));
    car.position.add(localBack);
    car.updateWorldMatrix(true, true);
  }
}

// One switch for a cull item, batched or not. Only writes on a CHANGE — a
// BatchedMesh re-sorts its draw list when visibility moves, so rewriting the
// same value every tick would be work for nothing.
function setShown(it, v) {
  it.shown = v;
  if (it.slots) { for (const [b, id] of it.slots) b.setVisibleAt(id, v); }
  else it.o.visible = v;
}

export default function CityModel({ night = 0 }) {
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const { scene } = useGLTF(MODEL_URL, true, true, ktx2);
  const model = useMemo(() => scene.clone(true), [scene]);
  // Mobile: when the city unmounts (entering an interior), DUMP its GPU memory so
  // a Gaussian splat has room to load without OOM-crashing iOS. It reloads (from
  // HTTP cache) on return to the street. Desktop keeps it cached (dispose={null}).
  useEffect(() => {
    if (!IS_MOBILE) return undefined;
    return () => freeGltf(model, MODEL_URL);
  }, [model]);
  // DRAW THE CITY IN BATCHES (utils/cityBatch). Built before the first frame
  // this model is drawn in, torn down with it. The originals stay in the graph,
  // named and raycastable — every lookup below keeps working unchanged.
  //
  // Excluded: the video signs (re-materialed once the clips arrive). The hero
  // Lotus is resized before batching rather than left out.
  const batchRef = useRef(null);
  // ANISOTROPIC FILTERING on every city texture, set before the first upload.
  // The GLB's textures load at anisotropy 1, which is right for something
  // viewed head-on and wrong for a STREET: the road, the pavement and every
  // shopfront are seen at a grazing angle from eye height, and plain trilinear
  // filtering smears them into mush a few metres out. 8x on desktop, 4x on
  // phones — a sampler setting, so no extra memory and no re-encode.
  useLayoutEffect(() => {
    const max = gl.capabilities.getMaxAnisotropy?.() || 1;
    const want = Math.min(max, IS_MOBILE ? 4 : 8);
    if (want <= 1) return;
    const seen = new Set();
    model.traverse((o) => {
      if (!o.isMesh) return;
      for (const m of (Array.isArray(o.material) ? o.material : [o.material])) {
        if (!m) continue;
        for (const k of ['map', 'emissiveMap', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap']) {
          const t = m[k];
          if (!t || seen.has(t)) continue;
          seen.add(t);
          if (t.anisotropy < want) { t.anisotropy = want; if (t.version > 0) t.needsUpdate = true; }
        }
      }
    });
  }, [model, gl]);
  // THE STREET'S OWN LIGHT. Every city material learns to read the lamp-pool
  // texture (and the road learns to get wet) before its first compile — the
  // patch changes the shader, so it has to land while the loading screen is
  // still up, not when the street lights finish reading the map. Until then
  // the pool texture is a single black texel; filling it later is a uniform
  // change, never a recompile.
  useLayoutEffect(() => {
    if (!NEXT_LOOK) return;
    const seen = new Set();
    model.traverse((o) => {
      if (!o.isMesh) return;
      for (const m of (Array.isArray(o.material) ? o.material : [o.material])) {
        if (!m || seen.has(m)) continue;
        seen.add(m);
        // Every surface can get wet in the rain; the night sheen is asphalt-only
        // (the shader tells asphalt from paving by its albedo).
        patchStreetMaterial(m, { wet: true });
      }
    });
    // Relief derived from the atlases themselves (render/detailNormals).
    if (QUALITY.normalsOn) {
      const t0 = performance.now();
      const n = applyDerivedNormals(gl, model);
      console.log(`[city] derived normal maps for ${n} materials in ${Math.round(performance.now() - t0)} ms (?nm=0 to compare)`);
    }
  }, [model, gl]);
  useLayoutEffect(() => {
    if (CITY_BATCH_OFF) return undefined;
    // The car is sized FIRST — its matrices are baked into the batch below.
    scaleHeroCar(findHeroCar(model));
    const t0 = performance.now();
    const res = buildCityBatches(model, (o) => !!signBaseFor(o.name));
    if (!res) return undefined;
    for (const b of res.batches) model.add(b);
    // THE CITY TAKES PART IN THE SUN NOW. Nothing in the model received a shadow
    // before (ShadowGovernor's audit) — so the key light cast onto nothing and
    // the streets were lit like a showroom. Under the new look every batch both
    // casts and receives, and the light rig's follow-camera frustum keeps the
    // shadows crisp wherever you are standing.
    if (NEXT_LOOK && QUALITY.shadowSize) {
      // Flat ground can't shadow anything but itself, so the road batches only
      // receive — a whole layer of the city kept out of the shadow pass.
      for (const b of res.batches) { b.receiveShadow = true; b.castShadow = b.material?.name !== 'RoadsGround'; }
      // The few city meshes that still draw as themselves (video signs, glass)
      // join in the ordinary way.
      model.traverse((o) => { if (o.isMesh && o.visible && !o.userData.cityBatch) { o.castShadow = true; o.receiveShadow = true; } });
    }
    batchRef.current = res;
    cullRef.current = null;   // the cull re-derives its list against the batches
    console.log(`[city] ${res.stats.meshes} meshes → ${res.stats.batches} draw batches (${res.stats.instances} instances) in ${Math.round(performance.now() - t0)}ms. ?batch=0 to compare.`);
    return () => { clearCity(model); disposeCityBatches(res); batchRef.current = null; cullRef.current = null; };
  }, [model]);
  // Publish the batched city for the systems that read it (street lights, the
  // radar's road map). Waits for the fitted bounds: the map is drawn in world
  // units, so it needs to know where the city is.
  const boundsNow = useWorldStore((s) => s.cityBounds);
  useEffect(() => {
    if (!batchRef.current || !boundsNow) return;
    publishCity(model, batchRef.current.batches, boundsNow);
  }, [model, boundsNow]);
  const nightRef = useRef(night);
  nightRef.current = night; // 0 (day) → 1 (night): the beat pulse fades in with dusk
  const setLandmark = useWorldStore((s) => s.setLandmark);
  // Hand-placed venue anchors, loaded once and kept in a ref so the big
  // measuring effect below can read them without listing them as a dependency —
  // it traverses the whole city model, and re-running it because an unrelated
  // store slice changed would be a real cost. It DOES list the override version
  // counter, so a save still re-derives the anchors immediately.
  const setVenueAnchorOverrides = useWorldStore((s) => s.setVenueAnchorOverrides);
  const overrides = useWorldStore((s) => s.venueAnchorOverrides);
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;
  // BOTH ROWS, OR THE WORK IS LOST ON RELOAD.
  //
  // A venue's placement is two rows — `venue_anchor` for where the building is,
  // `venue_shell` for the exterior's own height and size trim. Only the first
  // was being loaded, so the shell alignment saved correctly, came back from the
  // API correctly, and was then simply never read: it wrote to a store slice
  // nothing populated at boot. From the outside that is indistinguishable from
  // "the save didn't work", which is exactly how it was reported.
  const setVenueShellOverrides = useWorldStore((s) => s.setVenueShellOverrides);
  useEffect(() => {
    let dead = false;
    listWorldObjects('street', 'venue_anchor').then((rows) => {
      if (dead || !rows?.length) return;
      const map = {};
      for (const r of rows) map[r.ref] = { x: r.pos[0], y: r.pos[1] || 0, z: r.pos[2], scale: r.size || 1, rotY: r.rotY || 0 };
      setVenueAnchorOverrides(map);
      console.log(`[venue] ${rows.length} hand-placed anchor(s) loaded — these override the sampler`);
    });
    listWorldObjects('street', 'venue_shell').then((rows) => {
      if (dead || !rows?.length) return;
      const map = {};
      for (const r of rows) map[r.ref] = { y: r.pos[1] || 0, scale: r.size || 1 };
      setVenueShellOverrides(map);
      console.log(`[venue] ${rows.length} shell trim(s) loaded — ${rows.map((r) => `${r.ref} y${(r.pos[1] || 0) > 0 ? '+' : ''}${(r.pos[1] || 0).toFixed(2)} x${(r.size || 1).toFixed(2)}`).join(', ')}`);
    });
    return () => { dead = true; };
  }, [setVenueAnchorOverrides, setVenueShellOverrides]);

  const setVenueAnchors = useWorldStore((s) => s.setVenueAnchors);
  const setVenueBoxes = useWorldStore((s) => s.setVenueBoxes);
  const setCityBounds = useWorldStore((s) => s.setCityBounds);
  const setCityFit = useWorldStore((s) => s.setCityFit);
  const setPicked = useWorldStore((s) => s.setPicked);
  const PICK_PARAM = useMemo(() => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('pick') === '1', []);
  // The building grid picks its brush this way, so the tool turns picking on
  // itself. Depending on ?pick=1 meant the brush quietly stopped working the
  // moment that param fell out of a URL you were editing for something else —
  // and the panel told you to add a flag rather than just working.
  const BLOCKS = useWorldStore((s) => s.adminTool) === 'blocks';
  const brush = useWorldStore((s) => s.picked);
  const PICK = PICK_PARAM || BLOCKS;

  // ?pick=1: click any mesh to identify it (name, material, size) so we can
  // target a specific sign for dynamic graphics.
  const onPick = (e) => {
    e.stopPropagation();
    const o = e.object;
    const b = new THREE.Box3().setFromObject(o);
    const s = new THREE.Vector3(); b.getSize(s);
    const r = (v) => Math.round(v * 10) / 10;
    setPicked({ name: o.name || '(unnamed)', material: o.material?.name || '(none)', size: [r(s.x), r(s.y), r(s.z)] });
  };

  // HypeClips → played on the tall (portrait) city signs. Each sign loops a
  // muted 9:16 clip; clicking it unmutes that clip (single-source audio).
  const [clips, setClips] = useState([]);
  const signMap = useRef({});
  useEffect(() => {
    api.hypeClips().then((d) => setClips((d?.clips || []).filter((c) => c.gcs_url))).catch(() => setClips([]));
  }, []);
  useEffect(() => {
    if (!clips.length) return undefined;
    const map = {};
    const videos = [];
    let i = 0;
    // Which clip lands on which sign was `i % clips.length` — walk order, fixed
    // forever, so the same sign carried the same clip on every visit and the
    // city never changed between sessions. A per-load offset shuffles the whole
    // assignment: the signs still each get a different clip, but WHICH one moves.
    //
    // Deliberately per LOAD rather than on a timer like the billboards: these
    // are playing videos with decoders attached, and swapping their sources
    // mid-session would stutter every sign at once for no gain.
    const shift = Math.floor(Math.random() * clips.length);
    model.traverse((o) => {
      if (!o.isMesh || !signBaseFor(o.name)) return;
      const clip = clips[(i + shift) % clips.length];
      i += 1;
      const v = paintSign(o, { videoUrl: clip.gcs_url });
      if (v) { videos.push(v); map[o.name] = { clip, video: v }; }
    });
    signMap.current = map;
    return () => { videos.forEach((v) => { try { v.pause(); v.removeAttribute('src'); v.load(); } catch { /* noop */ } }); };
  }, [model, clips]);

  const onClickModel = (e) => {
    // A wired sign always plays (even in ?pick=1 mode); otherwise pick logs it.
    const entry = signMap.current[e.object?.name];
    if (entry?.video) { e.stopPropagation(); audioEngine.playVideo(entry.video); return; }
    // In the building grid, shift-click PICKS only while no brush is held —
    // once one is, the same chord paints instead (BlockGrid owns that). Esc
    // drops the brush and hands picking back. A plain click is always free, so
    // the camera can be moved without placing anything.
    if (BLOCKS && (!e.shiftKey || brush?.name)) return;
    if (PICK) onPick(e);
  };

  // Beat pulse — the city's own GLOW lights (its emissive materials: windows,
  // signage, street/traffic lamps baked into the model) breathe with the song,
  // like the old world's lights. Collect the emissive materials once (deduped;
  // the dynamic video signs are excluded — they carry their own footage), cache
  // each one's base intensity, then swell them a single gentle hump per beat.
  const glowMats = useRef([]);
  useEffect(() => {
    const seen = new Map(); // material.uuid → { mat, base }
    model.traverse((o) => {
      if (!o.isMesh || signBaseFor(o.name)) return;
      const list = Array.isArray(o.material) ? o.material : [o.material];
      list.forEach((m) => {
        if (!m || seen.has(m.uuid)) return;
        const base = m.emissiveIntensity ?? 1;
        const lit = m.emissive && base > 0.01 && (m.emissive.r + m.emissive.g + m.emissive.b > 0.05 || m.emissiveMap);
        if (lit) seen.set(m.uuid, { mat: m, base });
      });
    });
    glowMats.current = [...seen.values()];
    return () => { glowMats.current.forEach(({ mat, base }) => { mat.emissiveIntensity = base; }); glowMats.current = []; };
  }, [model]);

  // BPM detection often lands an OCTAVE off (e.g. 184 for a 92-BPM song), which
  // reads as "faster than the tempo." Fold the analyzed bpm into a normal
  // tapping range so octave-errors self-correct and every song pulses near its
  // felt tempo — without breaking songs whose bpm is already right.
  //
  // Anchor the phase to the SONG PLAYHEAD (audioEngine.broadcastTime), not the
  // free-running render clock: it resets every track, so a slightly-off bpm
  // can't accumulate drift across a set (the "sways on and off beat" problem),
  // and peaks land on the song's own grid. Falls back to the render clock only
  // when the broadcast isn't the source (ambient pulse layer).
  useFrame(() => {
    // Pulse is an EVENING feature, starting at DUSK. The raw `night` factor is
    // only ~0.4 at dusk (20:00) and doesn't reach 1 until ~21:12, so keying the
    // pulse to it directly made the throb ramp in too late. Remap so it's ~off
    // through golden hour (night 0.05) and FULLY on by dusk (night 0.4).
    const pulseNight = Math.max(0, Math.min(1, (nightRef.current - 0.05) / 0.35));
    // Under the new look the windows also follow the hour on their own: offices
    // dim by day (the lit-window textures read as glowing at noon), and at night
    // they run hot enough for the bloom to catch. Same materials, one factor.
    const k = (1 + pulseNight * (beatPulse() * 1.3 - 0.5)) * (NEXT_LOOK ? sky.glowScale : 1);
    const list = glowMats.current;
    for (let i = 0; i < list.length; i++) list[i].mat.emissiveIntensity = list[i].base * k;
  });

  // DISTANCE CULL — stop drawing what the fog has already erased.
  //
  // The scene fogs to FULLY OPAQUE at 260 units while the camera's far plane sits
  // at 1200, so roughly four-fifths of the drawable depth is geometry that is
  // rendered and then completely painted over. It costs nothing on a plaza where
  // little is in frame, and everything down a long straight street, where the
  // corridor puts most of the city's ~1,350 meshes inside the frustum at once.
  //
  // Culling at the fog line is therefore VISUALLY LOSSLESS — not a quality tier,
  // just work we were doing for no picture. Measured per mesh against its own
  // bounding sphere so a building is kept while any part of it could still show,
  // and only a few times a second: the camera cannot cross 260 units between
  // ticks, and this is a loop over every mesh in the city.
  const cullRef = useRef(null);
  const cullClock = useRef(0);
  // Read live: the radius tightens the moment you step inside a sited venue and
  // opens back up when you leave.
  const zoomNow = useWorldStore((s) => s.zoom);
  // PLAN VIEW SWITCHES THE CULL OFF. It is a distance test from the camera, and
  // the plan camera sits 400+ units up — on mobile, where the radius is 275,
  // every mesh in the city would fail it and the map would come up empty. The
  // one view whose entire job is to show the whole city is also the one this
  // optimisation is guaranteed to erase.
  const planView = useWorldStore((s) => s.planView);
  const cullDist = planView ? 0 : (zoomNow === ZOOM.INTERIOR ? SITED_CULL : CULL_DIST);
  const cullDistRef = useRef(cullDist);
  cullDistRef.current = cullDist;
  useFrame((state, dt) => {
    const CULL = cullDistRef.current;
    if (!CULL) {
      // Coming back out: undo any culling before the radius goes to zero, or
      // whatever was hidden indoors stays hidden on the street.
      if (cullRef.current?.length && cullRef.current.hidden) {
        for (const it of cullRef.current) setShown(it, true);
        cullRef.current.hidden = false;
      }
      return;
    }
    cullClock.current += dt;
    if (cullClock.current < 0.2) return;
    cullClock.current = 0;

    // Built on the first tick, never in a memo: the meshes are children of a
    // <primitive> whose scale and position are applied at MOUNT, so world
    // matrices read before that are meaningless — the recurring trap in this file.
    if (!cullRef.current) {
      const items = [];
      model.updateMatrixWorld(true);
      const slots = batchRef.current?.slots;
      model.traverse((o) => {
        if (!o.isMesh || !o.geometry || o.userData.cityBatch) return;
        if (!o.geometry.boundingSphere) o.geometry.computeBoundingSphere();
        const bs = o.geometry.boundingSphere;
        if (!bs) return;
        const centre = bs.center.clone().applyMatrix4(o.matrixWorld);
        const s = new THREE.Vector3().setFromMatrixScale(o.matrixWorld);
        // A batched mesh is culled through its batch instance(s); the hidden
        // original must stay hidden whatever the distance says.
        items.push({ o, slots: slots?.get(o) || null, centre, r: bs.radius * Math.max(s.x, s.y, s.z), shown: true });
      });
      cullRef.current = items;
      if (items.length) console.log(`[city] distance cull armed — ${items.length} meshes, radius ${CULL}u`);
    }

    const cam = state.camera.position;
    const items = cullRef.current;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const want = cam.distanceTo(it.centre) - it.r <= CULL;
      if (want !== it.shown) setShown(it, want);
    }
    items.hidden = true;
  });

  const { scale, position, bounds } = useMemo(() => {
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3(); box.getSize(size);
    const center = new THREE.Vector3(); box.getCenter(center);
    const maxXZ = Math.max(size.x, size.z) || 1;
    const s = FIT_METRES / maxXZ;
    // Centre on the origin and drop the model's bottom to y=0.
    // The fitted footprint falls straight out of the same numbers: centred on 0,
    // so it is simply the scaled half-extents. The city is NOT square — fitting
    // the larger span to 240 leaves the other axis shorter — which is exactly
    // why a radius was the wrong shape to bound it with.
    const hx = (size.x * s) / 2, hz = (size.z * s) / 2;
    return {
      scale: s,
      position: [-center.x * s, -box.min.y * s, -center.z * s],
      bounds: { min: [-hx, -hz], max: [hx, hz], top: size.y * s },
    };
  }, [model]);

  // Report the parked car's world position (after the fit transform) so the
  // model fly-in can land beside it.
  useEffect(() => {
    const car = findHeroCar(model);
    if (!car) return;
    scaleHeroCar(car);

    const p = new THREE.Vector3();
    car.getWorldPosition(p); // walks parents incl. this model's scale/position
    setLandmark([p.x, p.y, p.z]);

    // Measure each candidate building: XZ centre + roof height (for the sign).
    const cand = [];
    for (const name of ANCHOR_CANDIDATES) {
      const node = model.getObjectByName(name);
      if (!node) continue;
      const box = new THREE.Box3().setFromObject(node);
      if (!isFinite(box.min.x)) continue;
      const c = new THREE.Vector3(); box.getCenter(c);
      const s = new THREE.Vector3(); box.getSize(s);
      cand.push({ name, x: c.x, z: c.z, top: box.max.y, center: [c.x, c.y, c.z], size: [s.x, s.y, s.z] });
    }
    // Measure ANY named node, not just a candidate — an explicit pin is allowed
    // to name a building the sampler was never offered.
    const measure = (name) => {
      const node = model.getObjectByName(name);
      if (!node) return null;
      const box = new THREE.Box3().setFromObject(node);
      if (!isFinite(box.min.x)) return null;
      const c = new THREE.Vector3(); box.getCenter(c);
      const s = new THREE.Vector3(); box.getSize(s);
      return { name, x: c.x, z: c.z, top: box.max.y, center: [c.x, c.y, c.z], size: [s.x, s.y, s.z] };
    };

    // ── EXPLICIT PINS WIN ────────────────────────────────────────────────────
    //
    // A room with `anchorMesh: '<mesh name>'` in rooms.js stands on THAT
    // building. The sampler below is a decent way to scatter venues across the
    // map when nobody has an opinion, but it cannot know that a shop wants a
    // corner unit on a street you can actually see down — so naming one has to
    // beat it outright.
    //
    // A MESH NAME, not coordinates, for the same reason the building brush uses
    // one: the city is auto-fitted to FIT_METRES, so every coordinate in it
    // moves if the model is ever re-exported or re-fitted, while the name does
    // not. Find one with ?pick=1 and shift-click the building.
    const pinned = {};
    const pinFailed = [];
    for (const key of VENUE_ORDER) {
      const want = ROOMS[key]?.anchorMesh;
      if (!want) continue;
      const m = measure(want);
      if (m) pinned[key] = m;
      else pinFailed.push(`${key}→"${want}"`);
    }
    if (pinFailed.length) {
      // Loud, because a typo'd name and no pin at all look identical from inside
      // the room — both leave the venue wherever the sampler put it.
      console.warn(`[venue] anchorMesh not found in the city model: ${pinFailed.join(', ')} — falling back to the sampler. Check the name with ?pick=1.`);
    }

    // Farthest-point sampling for whatever is left, so the unpinned venues stay
    // spread out and travelling between them is a real journey. Seeded on the
    // concert hall unless that one is pinned.
    //
    // Sampling runs for EVERY venue, not the first five — the cap used to be a
    // literal 5 against a seven-entry VENUE_ORDER, so the last two (grocery,
    // subway) silently got no building and fell through to hardcoded fallback
    // coordinates. That is invisible until a room is `sited`, at which point it
    // stands in open air next to the city rather than in it.
    const open = VENUE_ORDER.filter((k) => !pinned[k]);
    const dist2 = (a, b) => (a.x - b.x) ** 2 + (a.z - b.z) ** 2;
    let picks = [];
    // Pinned buildings still repel: an unpinned venue should not be sampled on
    // top of one somebody placed by hand.
    const taken = Object.values(pinned);
    const free = cand.filter((c) => !taken.some((t) => t.name === c.name));
    const seed = free.find((c) => c.name === CONCERT_SEED) || free[0];
    if (seed) {
      // With no pins at all there is nothing to spread FROM, so the seed starts
      // it. Any pin makes a better starting set than an arbitrary seed does.
      picks = taken.length ? [] : [seed];
      while (picks.length < open.length && picks.length < free.length) {
        const from = [...picks, ...taken];
        let best = null; let bestD = -1;
        for (const c of free) {
          if (picks.includes(c)) continue;
          const d = from.length ? Math.min(...from.map((p) => dist2(p, c))) : 0;
          if (d > bestD) { bestD = d; best = c; }
        }
        if (!best) break;
        picks.push(best);
      }
    }
    const anchors = {};
    const boxes = {};
    const chosen = {};
    let oi = 0;
    VENUE_ORDER.forEach((key) => {
      const p = pinned[key] || picks[oi++];
      anchors[key] = p ? [p.x, p.top, p.z] : ANCHOR_FALLBACK[key];
      if (p) boxes[key] = { center: p.center, size: p.size };
      chosen[key] = p ? `${p.name}${pinned[key] ? ' (pinned)' : ''}` : 'FALLBACK COORDS — no building';

      // A HAND-PLACED ANCHOR BEATS EVERYTHING. Saved in-world with the venue
      // brush (world_objects, kind 'venue_anchor'), so moving a venue is a save
      // rather than a rooms.js edit and a redeploy.
      //
      // Applied HERE, at the one place anchors are produced, so every consumer —
      // the markers, the spawn offset, MeshInterior's sited origin — picks it up
      // with no changes of its own. The building's box is deliberately left
      // pointing at whatever building was sampled, because after a manual move
      // there may be no building at all: the plan view then draws the interior's
      // footprint where you put it and no dashed shell, which is the truth.
      const ov = overridesRef.current?.[key];
      if (ov) {
        anchors[key] = [ov.x, anchors[key][1], ov.z];
        delete boxes[key];
        // The WHOLE placement, not just the ground position. The line used to
        // print x and z alone, which was fine when those were the only things a
        // hand placement could carry — but a venue now also holds a height
        // nudge, a scale and a facing, and a readout that silently omits three
        // of the five is worse than no readout: it reads as complete.
        chosen[key] = `moved by hand → ${ov.x.toFixed(1)}, ${ov.z.toFixed(1)}`
          + (ov.y ? ` · y ${ov.y > 0 ? '+' : ''}${ov.y.toFixed(2)}` : '')
          + (ov.scale && ov.scale !== 1 ? ` · x${ov.scale.toFixed(2)}` : '')
          + (ov.rotY ? ` · ${Math.round(((ov.rotY * 180) / Math.PI % 360 + 360) % 360)}°` : '');
      }
    });
    setVenueAnchors(anchors);
    setVenueBoxes(boxes);
    // Which building each venue ended up on, recorded as it is decided rather
    // than reverse-matched afterwards on float coordinates. Without this the only
    // way to find out where a venue landed is to walk there.
    console.log('[venue] anchors — ' + VENUE_ORDER.map((k) => `${k}: ${chosen[k]}`).join(' · '));

    // NO CLEANUP HERE — see the unmount effect below.
    //
    // This used to null the anchors on teardown, which is right for an unmount
    // and wrong for a dependency change: every re-run then published
    // anchors → null → anchors, so anything reading them saw the venues vanish
    // to the origin and come back. That is two extra state changes and a visible
    // jump per re-run, and it is the churn that turned one saved placement into
    // a wall of repeated log lines.
    //
    // `overrides` is in the deps so a saved move re-derives the anchors at once
    // (read through the ref, so the identity is all that matters here), and NOT
    // in it as a raw object read — that is what keeps an unrelated store write
    // from re-traversing the whole city model.
  }, [model, scale, position, overrides, setLandmark, setVenueAnchors, setVenueBoxes]);

  // WALLS. The street's collision BVH, built from the same frozen originals the
  // batches draw (game/streetCollision). After the anchors effect above, so the
  // hero car has been resized and its collider matches what you see.
  useEffect(() => {
    if (FEEL_CLASSIC) return undefined;
    scaleHeroCar(findHeroCar(model));
    buildStreetCollision(model);
    return () => clearStreetCollision();
  }, [model]);

  // Clear on UNMOUNT ONLY. Empty deps, so leaving the city is the only thing
  // that wipes these — not every recompute of them.
  useEffect(() => () => {
    setLandmark(null); setVenueAnchors(null); setVenueBoxes(null);
  }, [setLandmark, setVenueAnchors, setVenueBoxes]);

  useEffect(() => { setCityBounds(bounds); return () => setCityBounds(null); }, [bounds, setCityBounds]);
  useEffect(() => { setCityFit({ scale, position }); return () => setCityFit(null); }, [scale, position, setCityFit]);

  // dispose={null}: this is a clone(true) of useGLTF's CACHED scene, so it SHARES
  // geometries/materials with the cache. Entering an interior unmounts the street
  // branch; without this, r3f would dispose those shared resources on unmount and
  // buildings (e.g. the studio) would come back invisible after stepping out.
  // `isCityModel` tags this root so tools can raycast the BUILDINGS rather than
  // the whole scene. A name would be fragile (the GLB owns its own names); a
  // userData flag is ours and survives re-export.
  return <primitive object={model} userData={{ isCityModel: true }} scale={scale} position={position} onClick={onClickModel} dispose={null} />;
}

// BOTH builds now carry KTX2 textures, and useGLTF.preload() cannot parse those:
// the KTX2Loader needs a live WebGLRenderer to choose a GPU format, and there is
// no renderer at module scope. Preloading here threw
// "setKTX2Loader must be called before loading KTX2 textures" and took the whole
// Canvas down with it.
//
// A plain fetch gets the same head start without decoding anything — it warms
// the HTTP cache so the in-component load (which DOES have the loader, via
// useThree's gl) parses from memory instead of the network.
// The body is READ, not just requested: an unconsumed response is not reliably
// written to the HTTP cache, which would make the warm-up a no-op.
fetch(MODEL_URL).then((r) => r.arrayBuffer()).catch(() => { /* the real load reports failures */ });
